import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Loader2, FileText, Search, Building2 } from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { toast } from 'sonner';

function money(v) {
  const n = Number(v || 0);
  return `${n < 0 ? '-$' : '$'}${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const SIGN_FILTERS = [
  { key: 'all',      label: 'All',      params: {} },
  { key: 'positive', label: 'Positive', params: { sign: 'positive' } },
  { key: 'negative', label: 'Negative', params: { sign: 'negative' } },
  { key: 'zero',     label: 'Zero',     params: { sign: 'zero' } },
];

export default function StatementsListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [signFilter, setSignFilter] = useState('all');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: 1, page_size: 100,
        ...(SIGN_FILTERS.find((s) => s.key === signFilter)?.params || {}),
        ...(q.trim() ? { q: q.trim() } : {}),
      };
      const { data } = await accountingAPI.listStatements(params);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load statements');
    } finally { setLoading(false); }
  }, [signFilter, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page-content" data-testid="statements-list-page">
      <PageHeader
        title="Statements"
        description="Immutable internal payment records generated from Carrier Imports."
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Accounting', href: '/accounting/carrier-imports' },
          { label: 'Statements' },
        ]}
      />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search # / Recipient / Carrier"
            className="pl-8 h-9"
            data-testid="statements-search"
          />
        </div>
        <div className="flex gap-1">
          {SIGN_FILTERS.map((s) => (
            <Badge key={s.key}
                   variant={signFilter === s.key ? 'default' : 'outline'}
                   className="cursor-pointer text-xs"
                   onClick={() => setSignFilter(s.key)}
                   data-testid={`filter-${s.key}`}>
              {s.label}
            </Badge>
          ))}
        </div>
        <div className="ml-auto text-xs text-slate-500">{total} statement(s)</div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
          ) : rows.length === 0 ? (
            <div className="p-16 text-center text-slate-500">
              <FileText className="mx-auto mb-2 text-slate-300" size={22} />
              <div>No statements match.</div>
              <div className="text-xs text-slate-400 mt-1">Generate statements from a reconciled Carrier Import.</div>
            </div>
          ) : (
            <table className="w-full text-xs" data-testid="statements-list-table">
              <thead>
                <tr className="border-b bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                  <th className="py-2 px-3">Statement #</th>
                  <th className="py-2 px-3">Carrier</th>
                  <th className="py-2 px-3">Recipient</th>
                  <th className="py-2 px-3">Period</th>
                  <th className="py-2 px-3">Generated</th>
                  <th className="py-2 px-3">Lines</th>
                  <th className="py-2 px-3 text-right">Net Total</th>
                  <th className="py-2 px-3 text-right">Remaining</th>
                  <th className="py-2 px-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.statement_id}
                      onClick={() => navigate(`/accounting/statements/${s.statement_id}`)}
                      className="border-b border-slate-50 hover:bg-slate-50/60 cursor-pointer"
                      data-testid={`statement-list-row-${s.statement_id}`}>
                    <td className="py-2 px-3 font-mono font-semibold text-slate-800">{s.statement_number}</td>
                    <td className="py-2 px-3">{s.carrier_name || '—'}</td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-1.5">
                        <Building2 size={11} className="text-slate-400" />
                        <div>
                          <div className="font-medium">{s.recipient_name}</div>
                          <div className="text-[10px] text-slate-500 capitalize">{s.recipient_type}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3">{s.period_label}</td>
                    <td className="py-2 px-3 text-slate-500">{(s.generated_at || '').slice(0, 10)}</td>
                    <td className="py-2 px-3">{s.total_lines}</td>
                    <td className={`py-2 px-3 text-right font-mono font-semibold ${s.net_total < 0 ? 'text-red-700' : 'text-slate-800'}`}>
                      {money(s.net_total)}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{money(s.remaining_payable_amount)}</td>
                    <td className="py-2 px-3">
                      <Badge className={s.status === 'negative_balance'
                          ? 'bg-red-100 text-red-800 text-[10px] py-0 px-1.5'
                          : 'bg-emerald-100 text-emerald-800 text-[10px] py-0 px-1.5'}>
                        {s.status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
